import React, { useState } from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import Svg, { Path, Circle, Line, G } from 'react-native-svg';
import { ChevronDown, ChevronUp } from 'lucide-react-native';
import {
  useTheme,
  SPACING,
  BORDER_RADIUS,
  TYPOGRAPHY,
  ELEVATION,
  HOUSE_PATH,
  TRIANGLE_PATH,
  COMMUNITY_BADGE_PATH,
  MAP_CLUSTER_FILL,
  PROPERTY_CLUSTER_FILL,
  withWeight,
} from '../../theme';
import type { ThemeColors } from '../../theme/colors';
import { READINESS_LABELS } from '../../utils/readiness';
import {
  useUIStore,
  useLeftDockInset,
  useObservationsVisible,
  selectMapKeyShown,
} from '../../store/uiStore';
import { useDefensePlansStore } from '../../store/defensePlansStore';
import { useTabBarClearance } from '@hooks/useTabBarClearance';
import { useDesktopShell } from '@hooks/useDesktopShell';
import { TRANSITION_DOCK_INSET } from '../../utils/webStyles';
import { FHSZ_SOURCE_LABEL, FHSZ_TIER_COLORS, FHSZ_TIER_ORDER } from './fhszColors';
import {
  FIRE_ERA_LABELS,
  FIRE_ERA_ORDER,
  FIRE_ERA_STYLE,
  FIRE_HISTORY_SOURCE_LABEL,
  FIRE_HISTORY_STROKE,
  type FireEra,
} from './fireHistoryColors';
import { COMMUNITY_PROGRAMS, COMMUNITY_PROGRAM_ORDER } from './communityPrograms';
import {
  DEFENSE_STRATEGIES,
  DEFENSE_STRATEGY_ORDER,
  PLAN_LINE_WEIGHT,
  strategyBadgeColors,
} from './defenseStrategies';
import { strategyCounts } from '../../utils/planGeometry';
import type { DefenseStrategy } from '../../types';

/**
 * The map's key.
 *
 * The map encodes two things at once: shape says what a marker is, color says
 * how far along a property is (see getStructureMarkerColor). Neither is
 * guessable, and until this existed nothing on screen said so.
 *
 * Collapsed by default on phones, where the map is the whole screen and every
 * corner is already spoken for — a tap expands/collapses it there, same as
 * always. On the desktop shell (Sept 2026 A2) it's shown or absent outright
 * instead, toggled from the map-tools rail's Key row (uiStore.mapKeyOverride/
 * toggleMapKey) rather than collapsed in place; when shown it's always fully
 * expanded, no chevron. Either way it sits bottom-left, the one corner the
 * search bar, the sign-in chip, the map controls and the tab bar all leave
 * alone.
 */

interface SwatchProps {
  color: string;
  colors: ThemeColors;
}

function HouseSwatch({ color, colors }: SwatchProps) {
  return (
    <Svg width={16} height={16} viewBox="0 0 24 24">
      <Path
        d={HOUSE_PATH}
        fill={color}
        stroke={colors.surfaceElevated}
        strokeWidth={2}
        strokeLinejoin="round"
      />
    </Svg>
  );
}

function ObservationSwatch({ color, colors }: SwatchProps) {
  return (
    <Svg width={16} height={16} viewBox="0 0 24 24">
      <Circle cx={12} cy={12} r={9} fill={color} stroke={colors.surfaceElevated} strokeWidth={2} />
    </Svg>
  );
}

// A property cluster as the map draws it (.sc-cluster): a slate house in a
// pale halo of the same color.
function PropertyClusterSwatch({ colors }: Pick<SwatchProps, 'colors'>) {
  return (
    <Svg width={16} height={16} viewBox="0 0 24 24">
      <Circle cx={12} cy={12} r={11} fill={PROPERTY_CLUSTER_FILL} fillOpacity={0.25} />
      <G transform="translate(4.8 4.8) scale(0.6)">
        <Path
          d={HOUSE_PATH}
          fill={PROPERTY_CLUSTER_FILL}
          stroke={colors.surfaceElevated}
          strokeWidth={2}
          strokeLinejoin="round"
        />
      </G>
    </Svg>
  );
}

function DeltaSwatch({ color, colors }: SwatchProps) {
  return (
    <Svg width={16} height={16} viewBox="0 0 24 24">
      <Path
        d={TRIANGLE_PATH}
        fill={color}
        stroke={colors.surfaceElevated}
        strokeWidth={2.5}
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/**
 * The community program marker: a hexagon with the program's letter in its
 * corner. Both layers draw the same shape in the same color, so the letter
 * is what tells a Firewise site from a fire safe council, exactly as it does
 * on the map.
 */
function CommunityProgramSwatch({ color, colors, badge }: SwatchProps & { badge: string }) {
  return (
    <View style={styles.communitySwatch}>
      <Svg width={16} height={16} viewBox="0 0 24 24">
        <Path
          d={COMMUNITY_BADGE_PATH}
          fill={color}
          stroke={colors.surfaceElevated}
          strokeWidth={2}
          strokeLinejoin="round"
        />
      </Svg>
      <Text
        style={[styles.communitySwatchBadge, { color, backgroundColor: colors.surfaceElevated }]}>
        {badge}
      </Text>
    </View>
  );
}

function ColorChipSwatch({ fill, stroke }: { fill: string; stroke: string }) {
  return <View style={[styles.colorChip, { backgroundColor: fill, borderColor: stroke }]} />;
}

/**
 * A short line at the weight and opacity the map draws that era at. The
 * fire history layer is strokes, not fills, so a filled chip would be a
 * swatch of something that isn't on the map.
 */
function StrokeSwatch({ era }: { era: FireEra }) {
  const { weight, opacity, dashArray } = FIRE_ERA_STYLE[era];
  return (
    <Svg width={14} height={weight + 2}>
      <Line
        x1={0}
        y1={(weight + 2) / 2}
        x2={14}
        y2={(weight + 2) / 2}
        stroke={FIRE_HISTORY_STROKE}
        strokeWidth={weight}
        strokeOpacity={opacity}
        strokeDasharray={dashArray}
      />
    </Svg>
  );
}

/**
 * A short line at the plan layer's stroke weight, honouring each strategy's
 * dash pattern — the same reasoning as StrokeSwatch above, for the same
 * reason: the line/point plan layers draw strokes, not fills.
 */
function PlanStrokeSwatch({ strategy }: { strategy: DefenseStrategy }) {
  const def = DEFENSE_STRATEGIES[strategy];
  // Same dark-mode lightening the panel badges use, so a near-black stroke
  // (fireproof paint, metal fences) doesn't vanish on the dark key.
  const { isDark } = useTheme();
  const color = strategyBadgeColors(strategy, isDark).icon;
  const height = PLAN_LINE_WEIGHT + 2;
  return (
    <Svg width={14} height={height}>
      <Line
        x1={0}
        y1={height / 2}
        x2={14}
        y2={height / 2}
        stroke={color}
        strokeWidth={PLAN_LINE_WEIGHT}
        strokeDasharray={def.dashArray}
      />
    </Svg>
  );
}

/**
 * A filled square in the strategy's colour — the area feature's own shape
 * (shape says what a marker/feature is, same grammar the pin legend rows
 * follow), distinct from PlanStrokeSwatch's line so a plan mixing area and
 * line features for the same strategy still reads two different things on
 * the map.
 */
function PlanAreaSwatch({ strategy }: { strategy: DefenseStrategy }) {
  const { isDark } = useTheme();
  const color = strategyBadgeColors(strategy, isDark).icon;
  return <View style={[styles.colorChip, { backgroundColor: color, borderColor: color }]} />;
}

interface RowProps {
  swatch: React.ReactNode;
  label: string;
  colors: ThemeColors;
}

function LegendRow({ swatch, label, colors }: RowProps) {
  return (
    <View style={styles.row}>
      <View style={styles.swatch}>{swatch}</View>
      <Text style={[styles.rowLabel, { color: colors.textSecondary }]}>{label}</Text>
    </View>
  );
}

export function MapLegend() {
  const { colors } = useTheme();
  const isDesktopShell = useDesktopShell();
  const tabBarClearance = useTabBarClearance();
  // The key sits bottom-left, the one corner a left-docked panel (the
  // defense plan editor/viewer) now covers — shift clear of it the same
  // way right-anchored chrome shifts clear of a right-docked one. 0 off the
  // desktop shell and whenever nothing is docked left.
  const leftDockInset = useLeftDockInset();
  // Same gate MapControls and MapSignInOverlay use: any sheet or dialog holds
  // the tab bar down, and while one is open the user is not reading the map.
  // Without this the key showed through the scrim behind the tour and the
  // auth gate on the desktop shell, and on phones it jumped to the bottom of
  // the screen as the clearance collapsed to 0.
  const tabBarVisible = useUIStore((s) => s.tabBarVisible);
  const activeSheet = useUIStore((s) => s.activeSheet);
  // While a moderator is placing a plan map, the placement editor's corner
  // handle sits in this same bottom-left corner — the key has to get out of
  // its way entirely, not just avoid the dock inset shift the rest of this
  // component handles.
  const placingOverlayId = useUIStore((s) => s.placingOverlayId);
  // Both platforms hide property markers at wide zoom, and the map opens
  // wide, so the first thing most users see is a map with no houses on it and
  // nothing saying why. Written by the render gates themselves — see
  // uiStore.propertyPinsVisible.
  const propertyPinsVisible = useUIStore((s) => s.propertyPinsVisible);
  const observationsVisible = useObservationsVisible();
  // Web-only overlays, switched on in the map layers sheet — the key grows a
  // section for each only while it's actually drawn on the map.
  const fhszOverlayEnabled = useUIStore((s) => s.fhszOverlayEnabled);
  const fireHistoryEnabled = useUIStore((s) => s.fireHistoryEnabled);
  const firewiseEnabled = useUIStore((s) => s.firewiseEnabled);
  const fireSafeCouncilsEnabled = useUIStore((s) => s.fireSafeCouncilsEnabled);
  // The plan the map is actually drawing: someone else's opened plan takes
  // priority, else the owner's own plan while the editor is docked. Mirrors
  // the rendering rule useDefensePlanLayer.web.ts follows.
  const viewedPlan = useDefensePlansStore((s) => s.viewedPlan);
  const ownPlan = useDefensePlansStore((s) => s.ownPlan);
  const planPanelOpen = useUIStore((s) => s.planPanelOpen);
  const candidatePlan = viewedPlan ?? (planPanelOpen ? ownPlan : null);
  // An emptied plan keeps its doc (so Undo and share links keep the id) but
  // has nothing on the map, so it adds no "Defense plan" section either.
  const renderedPlan = candidatePlan && candidatePlan.features.length > 0 ? candidatePlan : null;
  const planStrategyCounts = renderedPlan ? strategyCounts(renderedPlan.features) : {};
  // Which strategies have at least one area feature in the rendered plan —
  // decides PlanAreaSwatch vs PlanStrokeSwatch per row below. A strategy
  // with both an area and a line feature reads as an area: the filled
  // shape is the more informative swatch of the two.
  const planAreaStrategies = new Set(
    (renderedPlan?.features ?? [])
      .filter((feature) => feature.kind === 'area')
      .map((feature) => feature.strategy)
  );
  // Phone/tablet only from here down — null = follow neither, collapsed by
  // default (the map is the whole screen there and every corner is spoken
  // for). Desktop's shown/absent choice is mapKeyShown below instead; it
  // doesn't collapse, it disappears outright, so there's nothing here for
  // an override to seed.
  const [override, setOverride] = useState<boolean | null>(null);
  const expanded = override ?? false;
  // Desktop: shown or absent outright (uiStore.mapKeyOverride, via the
  // rail's Key row), not collapsed/expanded — the card used to stay up as
  // a collapsed header even while planning; now the whole thing goes away
  // by default there, and an explicit press brings it back regardless of
  // planPanelOpen. Known quirk, accepted: pressing Key while a structure is
  // open flips the preference but the card stays suppressed by the
  // `selectedStructure == null` gate downstream (app/(tabs)/index.tsx)
  // until the structure closes.
  const mapKeyShown = useUIStore(selectMapKeyShown);

  const Chevron = expanded ? ChevronUp : ChevronDown;

  if (!tabBarVisible || activeSheet !== null || placingOverlayId) return null;
  if (isDesktopShell && !mapKeyShown) return null;

  return (
    <View
      style={[
        styles.wrapper,
        TRANSITION_DOCK_INSET,
        { bottom: tabBarClearance + SPACING.md, left: SPACING.md + leftDockInset },
      ]}>
      <View
        style={[
          styles.container,
          {
            backgroundColor: colors.surfaceElevated,
            borderColor: colors.border,
          },
        ]}
        testID="map-legend">
        {isDesktopShell ? (
          // Static — no chevron, no collapse. The rail's Key row is the
          // one control for whether this card exists at all now.
          <View style={styles.header}>
            <HouseSwatch color={colors.progress} colors={colors} />
            <Text style={[styles.headerLabel, { color: colors.text }]}>Key</Text>
          </View>
        ) : (
          <Pressable
            onPress={() => setOverride(!expanded)}
            accessibilityRole="button"
            accessibilityState={{ expanded }}
            aria-expanded={expanded}
            accessibilityLabel={expanded ? 'Hide map key' : 'Show map key'}
            style={styles.header}
            testID="map-legend-toggle">
            <HouseSwatch color={colors.progress} colors={colors} />
            <Text style={[styles.headerLabel, { color: colors.text }]}>Key</Text>
            <Chevron size={14} color={colors.textTertiary} />
          </Pressable>
        )}

        {/* Outside the expanded/desktop block on purpose: the key is
            collapsed by default on phones, which is exactly where this
            line is needed. */}
        {!propertyPinsVisible && (
          <Text
            style={[styles.hint, { color: colors.textSecondary }]}
            testID="map-legend-zoom-hint">
            Zoom in for properties
          </Text>
        )}

        {(isDesktopShell || expanded) && (
          <View style={styles.body} testID="map-legend-body">
            <Text style={[styles.groupLabel, { color: colors.textTertiary }]}>Markers</Text>
            <LegendRow
              colors={colors}
              swatch={<HouseSwatch color={colors.mapMarkerAvailable} colors={colors} />}
              label="Property"
            />
            {/* Zoomed out, nearby homes merge into one numbered orange marker
                (useMarkerLayer.web.ts). The key left it out until the Sept 25
                2026 demo walk. */}
            <LegendRow
              colors={colors}
              swatch={<PropertyClusterSwatch colors={colors} />}
              label="Several properties"
            />
            {/* Observation rows follow the Observations layer (Layers sheet), so
                the key never explains markers that aren't on the map. */}
            {observationsVisible && (
              <>
                {/* One "Observation" row until Aug 2026, when the client asked for
                the two kinds by name. They carry the same words the post card
                uses ("Opportunity" / "Progress") and the same two colors the
                dots do. */}
                <LegendRow
                  colors={colors}
                  swatch={<ObservationSwatch color={colors.mapMarkerOpportunity} colors={colors} />}
                  label="Opportunity"
                />
                <LegendRow
                  colors={colors}
                  swatch={<ObservationSwatch color={colors.mapMarkerProgress} colors={colors} />}
                  label="Progress"
                />
                <LegendRow
                  colors={colors}
                  swatch={<ObservationSwatch color={colors.mapMarkerNews} colors={colors} />}
                  label="News"
                />
                <LegendRow
                  colors={colors}
                  swatch={<DeltaSwatch color={colors.mapMarkerDelta} colors={colors} />}
                  label="Before and after"
                />
                {/* Observations that sit on top of each other merge into one marker
                that splits again as you zoom in. A merged marker takes its
                category's color when most of its members share one; this row is
                the case where they don't, and without it the slate dots on the
                map are a symbol the key never explains. */}
                <LegendRow
                  colors={colors}
                  swatch={<ObservationSwatch color={MAP_CLUSTER_FILL} colors={colors} />}
                  label="Several, mixed kinds"
                />
              </>
            )}

            <Text
              style={[styles.groupLabel, styles.groupLabelSpaced, { color: colors.textTertiary }]}>
              Property color
            </Text>
            <LegendRow
              colors={colors}
              swatch={<HouseSwatch color={colors.progress} colors={colors} />}
              label={READINESS_LABELS.progress}
            />
            <LegendRow
              colors={colors}
              swatch={<HouseSwatch color={colors.partial} colors={colors} />}
              label={READINESS_LABELS.partial}
            />
            <LegendRow
              colors={colors}
              swatch={<HouseSwatch color={colors.risk} colors={colors} />}
              label={READINESS_LABELS.risk}
            />
            <LegendRow
              colors={colors}
              swatch={<HouseSwatch color={colors.neutral} colors={colors} />}
              label={READINESS_LABELS.none}
            />
            <LegendRow
              colors={colors}
              swatch={<HouseSwatch color={colors.mapMarkerAvailable} colors={colors} />}
              label="Unclaimed"
            />

            {fhszOverlayEnabled && (
              <>
                <Text
                  style={[
                    styles.groupLabel,
                    styles.groupLabelSpaced,
                    { color: colors.textTertiary },
                  ]}>
                  Fire hazard
                </Text>
                {FHSZ_TIER_ORDER.map((tier) => (
                  <LegendRow
                    key={tier}
                    colors={colors}
                    swatch={<ColorChipSwatch {...FHSZ_TIER_COLORS[tier]} />}
                    label={tier}
                  />
                ))}
                <Text style={[styles.sourceLine, { color: colors.textTertiary }]}>
                  {FHSZ_SOURCE_LABEL}
                </Text>
              </>
            )}

            {fireHistoryEnabled && (
              <>
                <Text
                  style={[
                    styles.groupLabel,
                    styles.groupLabelSpaced,
                    { color: colors.textTertiary },
                  ]}>
                  Fire history
                </Text>
                {FIRE_ERA_ORDER.map((era) => (
                  <LegendRow
                    key={era}
                    colors={colors}
                    swatch={<StrokeSwatch era={era} />}
                    label={FIRE_ERA_LABELS[era]}
                  />
                ))}
                <Text style={[styles.sourceLine, { color: colors.textTertiary }]}>
                  {FIRE_HISTORY_SOURCE_LABEL}
                </Text>
              </>
            )}

            {(firewiseEnabled || fireSafeCouncilsEnabled) && (
              <>
                <Text
                  style={[
                    styles.groupLabel,
                    styles.groupLabelSpaced,
                    { color: colors.textTertiary },
                  ]}>
                  Community programs
                </Text>
                {COMMUNITY_PROGRAM_ORDER.filter((id) =>
                  id === 'firewise' ? firewiseEnabled : fireSafeCouncilsEnabled
                ).map((id) => (
                  <View key={id}>
                    <LegendRow
                      colors={colors}
                      swatch={
                        <CommunityProgramSwatch
                          color={colors.mapMarkerCommunity}
                          colors={colors}
                          badge={COMMUNITY_PROGRAMS[id].badge}
                        />
                      }
                      label={COMMUNITY_PROGRAMS[id].singular}
                    />
                    <Text style={[styles.sourceLine, { color: colors.textTertiary }]}>
                      {COMMUNITY_PROGRAMS[id].sourceLabel}
                    </Text>
                  </View>
                ))}
              </>
            )}

            {renderedPlan && (
              <>
                <Text
                  style={[
                    styles.groupLabel,
                    styles.groupLabelSpaced,
                    { color: colors.textTertiary },
                  ]}>
                  Defense plan
                </Text>
                {DEFENSE_STRATEGY_ORDER.filter(
                  (strategy) => (planStrategyCounts[strategy] ?? 0) > 0
                ).map((strategy) => (
                  <LegendRow
                    key={strategy}
                    colors={colors}
                    swatch={
                      planAreaStrategies.has(strategy) ? (
                        <PlanAreaSwatch strategy={strategy} />
                      ) : (
                        <PlanStrokeSwatch strategy={strategy} />
                      )
                    }
                    label={DEFENSE_STRATEGIES[strategy].label}
                  />
                ))}
                <Text style={[styles.sourceLine, { color: colors.textTertiary }]}>
                  {renderedPlan.title} · {renderedPlan.ownerName}
                </Text>
              </>
            )}
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  // Owns the position: the `above` slot and the key's card both live inside
  // it, so a row passed into `above` sits directly over the card without
  // measuring it.
  wrapper: {
    position: 'absolute',
    // `left` is set inline (SPACING.md + leftDockInset) — see the render.
    // Under the search overlay (100) so an expanded key never covers address
    // results, above the map's own panes.
    zIndex: 50,
  },
  container: {
    borderRadius: BORDER_RADIUS.md,
    borderWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.xs,
    ...ELEVATION.marker,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    paddingVertical: SPACING.xs,
  },
  headerLabel: {
    ...TYPOGRAPHY.label,
  },
  body: {
    paddingBottom: SPACING.xs,
  },
  hint: {
    ...TYPOGRAPHY.caption,
    // Wraps to two lines rather than stretching the collapsed key, which is
    // otherwise about as wide as the word "Key".
    maxWidth: 150,
    paddingBottom: SPACING.xs,
  },
  groupLabel: {
    ...TYPOGRAPHY.overline,
    marginBottom: SPACING.xxs,
  },
  groupLabelSpaced: {
    marginTop: SPACING.sm,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    paddingVertical: 1,
  },
  swatch: {
    width: 16,
    alignItems: 'center',
  },
  rowLabel: {
    ...TYPOGRAPHY.caption,
  },
  colorChip: {
    width: 12,
    height: 12,
    borderRadius: BORDER_RADIUS.xs,
    borderWidth: 1,
  },
  sourceLine: {
    ...TYPOGRAPHY.caption,
    maxWidth: 150,
    marginTop: SPACING.xxs,
  },
  communitySwatch: {
    width: 16,
    height: 16,
    position: 'relative',
  },
  communitySwatchBadge: {
    ...withWeight(TYPOGRAPHY.caption, 'bold'),
    position: 'absolute',
    bottom: -3,
    right: -4,
    width: 10,
    height: 10,
    lineHeight: 10,
    borderRadius: 5,
    textAlign: 'center',
  },
});
